/**
 * Punch state hook — manages clock-in/out, active punch, elapsed time.
 * SDD §7.4 — usePunch.js
 *
 * State sources:
 *   - Active punch: GET /api/punch/status (authoritative, fetched on mount)
 *   - Elapsed time: Client-side setInterval from clock_in timestamp
 *   - Recent entries: GET /api/punch/history (last 5)
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch, ApiError } from '../lib/api';
import { setLastCategory, setLastPosition, setLastApparatus } from '../lib/storage';
import { queuePunch, isOnline, syncPendingPunches, initOfflineSync, captureLocation } from '../lib/offline';

// ── Types ────────────────────────────────────────────────────────────────

export interface ActivePunch {
  id: number;
  category: string;
  position: string | null;
  apparatus: string | null;
  clock_in: string;
  elapsed_seconds: number;
}

export interface RecentEntry {
  id: number;
  category: string;
  position: string | null;
  apparatus: string | null;
  clock_in: string;
  clock_out: string | null;
  duration_minutes: number | null;
  shift_type: string;
}

interface PunchStatusResponse {
  active: ActivePunch | null;
}

interface ClockInResponse {
  id: number;
  member_id: number;
  category: string;
  position: string | null;
  apparatus: string | null;
  clock_in: string;
  shift_type: string;
}

interface ClockOutResponse {
  id: number;
  clock_out: string;
  duration_minutes: number;
}

interface HistoryResponse {
  data: RecentEntry[];
  pagination: { page: number; limit: number; total: number; total_pages: number };
}

export interface ClockInPayload {
  category: string;
  position?: string;
  apparatus?: string;
  shift_type?: string;
  notes?: string;
}

export interface UsePunchReturn {
  active: ActivePunch | null;
  elapsedSeconds: number;
  recentEntries: RecentEntry[];
  loading: boolean;
  submitting: boolean;
  error: string | null;
  clockIn: (payload: ClockInPayload) => Promise<boolean>;
  clockOut: () => Promise<boolean>;
  clearError: () => void;
}

// ── Hook ─────────────────────────────────────────────────────────────────

export function usePunch(): UsePunchReturn {
  const [active, setActive] = useState<ActivePunch | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [recentEntries, setRecentEntries] = useState<RecentEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Elapsed time ticker ──────────────────────────────────────────────

  const startTimer = useCallback((clockInIso: string) => {
    if (timerRef.current) clearInterval(timerRef.current);
    const clockInMs = new Date(clockInIso).getTime();

    const tick = () => {
      setElapsedSeconds(Math.floor((Date.now() - clockInMs) / 1000));
    };
    tick();
    timerRef.current = setInterval(tick, 1000);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setElapsedSeconds(0);
  }, []);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  // ── Fetch initial state ──────────────────────────────────────────────

  const fetchStatus = useCallback(async () => {
    try {
      const data = await apiFetch<PunchStatusResponse>('/api/punch/status');
      setActive(data.active);
      if (data.active) {
        startTimer(data.active.clock_in);
      } else {
        stopTimer();
      }
    } catch {
      // Non-critical: status fetch failure doesn't block the UI
    }
  }, [startTimer, stopTimer]);

  const fetchRecent = useCallback(async () => {
    try {
      const data = await apiFetch<HistoryResponse>('/api/punch/history?limit=5');
      setRecentEntries(data.data);
    } catch {
      // Non-critical
    }
  }, []);

  useEffect(() => {
    async function init() {
      setLoading(true);
      // Initialize offline sync listeners (Sprint 14, FR-OFFLINE-02)
      initOfflineSync();
      await Promise.all([fetchStatus(), fetchRecent()]);
      // Sync any punches queued while offline
      if (isOnline()) syncPendingPunches();
      setLoading(false);
    }
    init();
  }, [fetchStatus, fetchRecent]);

  // ── Clock in ─────────────────────────────────────────────────────────

  const clockIn = useCallback(
    async (payload: ClockInPayload): Promise<boolean> => {
      setSubmitting(true);
      setError(null);
      try {
        // Capture GPS coordinates (Sprint 15, FR-GEO-01). Never blocks the punch.
        const location = await captureLocation();
        const body = location ? { ...payload, lat: location.lat, lng: location.lng } : payload;

        const data = await apiFetch<ClockInResponse>('/api/punch/in', {
          method: 'POST',
          body: JSON.stringify(body),
        });

        // Save remembered defaults
        setLastCategory(payload.category);
        if (payload.position) setLastPosition(payload.position);
        if (payload.apparatus) setLastApparatus(payload.apparatus);

        const newActive: ActivePunch = {
          id: data.id,
          category: data.category,
          position: data.position,
          apparatus: data.apparatus,
          clock_in: data.clock_in,
          elapsed_seconds: 0,
        };
        setActive(newActive);
        startTimer(data.clock_in);
        return true;
      } catch (err) {
        // Offline fallback: queue the punch locally (FR-OFFLINE-02)
        if (!isOnline() || (err instanceof TypeError && err.message === 'Failed to fetch')) {
          await queuePunch('in', payload as unknown as Record<string, unknown>);
          setLastCategory(payload.category);
          if (payload.position) setLastPosition(payload.position);
          if (payload.apparatus) setLastApparatus(payload.apparatus);
          setError(null);
          return true;
        }
        if (err instanceof ApiError) {
          setError(err.message);
        } else {
          setError('Failed to clock in. Please try again.');
        }
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [startTimer],
  );

  // ── Clock out ────────────────────────────────────────────────────────

  const clockOut = useCallback(async (): Promise<boolean> => {
    setSubmitting(true);
    setError(null);
    try {
      // Capture GPS coordinates at clock-out (Sprint 15, FR-GEO-01)
      const location = await captureLocation();
      const body = location ? { lat: location.lat, lng: location.lng } : {};

      await apiFetch<ClockOutResponse>('/api/punch/out', {
        method: 'POST',
        body: JSON.stringify(body),
      });

      setActive(null);
      stopTimer();

      // Refresh recent entries to include the just-closed entry
      await fetchRecent();
      return true;
    } catch (err) {
      // Offline fallback: queue the clock-out locally (FR-OFFLINE-02)
      if (!isOnline() || (err instanceof TypeError && err.message === 'Failed to fetch')) {
        await queuePunch('out', {});
        setActive(null);
        stopTimer();
        setError(null);
        return true;
      }
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Failed to clock out. Please try again.');
      }
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [stopTimer, fetchRecent]);

  // ── Clear error ──────────────────────────────────────────────────────

  const clearError = useCallback(() => setError(null), []);

  return {
    active,
    elapsedSeconds,
    recentEntries,
    loading,
    submitting,
    error,
    clockIn,
    clockOut,
    clearError,
  };
}
